<template>
  <v-dialog v-model="dialog" max-width="800px" scrollable>
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <span>Log Entry Details</span>
        <v-btn icon variant="text" @click="copyToClipboard">
          <v-icon>mdi-content-copy</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-card-text class="log-details">
        <div class="log-meta mb-3">
          <v-chip size="small" class="mr-2">{{ formatTs(logEntry.ts) }}</v-chip>
          <v-chip size="small" :color="levelColor" class="mr-2">{{ logEntry.level.toUpperCase() }}</v-chip>
          <v-chip size="small" variant="outlined" class="mr-2">{{ logEntry.source }}</v-chip>
        </div>
        
        <div class="log-message mb-3">
          <h4>Message:</h4>
          <div class="message-content">{{ logEntry.message }}</div>
        </div>
        
        <div v-if="logEntry.payload !== undefined" class="log-payload">
          <h4>Payload:</h4>
          <pre class="payload-content">{{ formatPayload(logEntry.payload) }}</pre>
        </div>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="copyToClipboard">
          <v-icon left>mdi-content-copy</v-icon>
          Copy to Clipboard
        </v-btn>
        <v-btn @click="dialog = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { DebugLogEntry } from '@/utils/debugBus'

interface Props {
  modelValue: boolean
  logEntry: DebugLogEntry
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const levelColor = computed(() => {
  switch (props.logEntry.level) {
    case 'error': return 'error'
    case 'warn': return 'warning'
    case 'event': return 'info'
    default: return undefined
  }
})

function formatTs(ts: number) {
  const d = new Date(ts)
  return d.toLocaleTimeString()
}

function formatPayload(payload: any) {
  try {
    return JSON.stringify(payload, null, 2)
  } catch {
    return String(payload)
  }
}

function formatLogEntry(): string {
  const ts = formatTs(props.logEntry.ts)
  const meta = `[${ts}] ${props.logEntry.level.toUpperCase()} ${props.logEntry.source}: ${props.logEntry.message}`
  
  if (props.logEntry.payload !== undefined) {
    const payload = formatPayload(props.logEntry.payload)
    return `${meta}\n\n${payload}`
  }
  
  return meta
}

async function copyToClipboard() {
  const text = formatLogEntry()
  
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    // Fallback for older browsers
    const el = document.createElement('textarea')
    el.value = text
    el.style.position = 'fixed'
    el.style.left = '-9999px'
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
  }
}
</script>

<style scoped>
.log-details {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 13px;
}

.log-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.message-content {
  background: rgba(0, 0, 0, 0.05);
  padding: 12px;
  border-radius: 4px;
  margin-top: 8px;
  font-weight: 600;
}

.payload-content {
  background: rgba(0, 0, 0, 0.05);
  padding: 12px;
  border-radius: 4px;
  margin-top: 8px;
  white-space: pre-wrap;
  overflow: auto;
  max-height: 400px;
}

h4 {
  margin-bottom: 4px;
  font-weight: 600;
}
</style>
